'use strict';

define(['$window'], function ($window) {

  var lazyloadIntersectionObserver = function lazyloadIntersectionObserver() {

    // Get all of the images that are marked up to lazy load
    var images = {};
    var config = {
      // If the image gets within 100px in the Y axis, start the download.
      rootMargin: '100px 0px',
      threshold: 0.5,
      imageSelector: '[data-image-type="lazy-image"]'
    };

    var imageCount = 0;
    var module = {};
    var observer = void 0;

    var _init = function _init() {

      images = document.querySelectorAll(config.imageSelector);
      imageCount = images.length;

      // If we don't have support for intersection observer, loads the images immediately
      if (!('IntersectionObserver' in window)) {
        module.loadImagesImmediately(images);
      } else {
        // It is supported, load the images
        module.checkLocalCache(); // first let's check the local cache!
        module.initialiseObserver();
      }
    };

    var _initialiseObserver = function _initialiseObserver() {
      observer = new IntersectionObserver(module.onIntersection, config);

      // foreach() is not supported in IE
      for (var i = 0; i < images.length; i++) {
        var image = images[i];
        if (image.getAttribute('data-image-type') === 'lazy-image--handled') {
          continue;
        }
        observer.observe(image);
      }
    };

    /**
     * Check the local cache first for images and load them if they exist already locally
     */

    var _checkLocalCache = function _checkLocalCache() {
      var allLazyLoadImages = Array.prototype.slice.call(images);

      Promise.all(allLazyLoadImages.map(function (img) {
        var src = img.getAttribute('data-src');

        return $window.caches.match(src).then(function (response) {
          if (response) {
            //looks like we already have this cached - winner
            module.preloadImage(img);
          }
        });
      }));
    };

    /**
     * Fetchs the image for the given URL
     * @param {string} url
     */
    var _fetchImage = function _fetchImage(url) {
      return new Promise(function (resolve, reject) {
        var image = new Image();
        image.src = url;
        image.onload = resolve;
        image.onerror = reject;
      });
    };

    /**
     * Preloads the image
     * @param {object} image
     */
    var _preloadImage = function _preloadImage(image) {
      var src = image.dataset.src;
      var srcset = image.dataset.srcset;
      if (!src && !srcset) {
        return;
      }

      if (src) {
        return module.fetchImage(src).then(function () {
          module.applyImage(image, src);
        });
      } else if (srcset) {
        return module.fetchImage(srcset).then(function () {
          module.applyImage(image, srcset);
        });
      }
    };

    /**
     * Load all of the images immediately
     * @param {NodeListOf<Element>} images
     */
    var _loadImagesImmediately = function _loadImagesImmediately(images) {
      // foreach() is not supported in IE
      for (var i = 0; i < images.length; i++) {
        var image = images[i];
        module.preloadImage(image);
      }
    };

    /**
     * Disconnect the observer
     */
    var _disconnect = function _disconnect() {
      if (!observer) {
        return;
      }

      observer.disconnect();
    };

    /**
     * On intersection
     * @param {array} entries
     */
    var _onIntersection = function _onIntersection(entries) {
      // Disconnect if we've already loaded all of the images
      if (imageCount === 0) {
        module.disconnect();
        return;
      }

      // Loop through the entries
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        // Are we in viewport?
        if (entry.intersectionRatio > 0) {
          imageCount--;

          // Stop watching and load the image
          observer.unobserve(entry.target);
          module.preloadImage(entry.target);
        }
      }
    };

    /**
     * Apply the image
     * @param {object} img
     * @param {string} src
     */
    var _applyImage = function _applyImage(img, src) {
      // Prevent this from being lazy loaded a second time.
      img.setAttribute('data-image-type', 'lazy-image--handled');
      if (img.dataset.src) {
        img.src = src;
      } else {
        img.srcset = src;
      }
      img.classList.add('js-lazy-image--fade-in');
    };

    module.init = _init;
    module.initialiseObserver = _initialiseObserver;
    module.checkLocalCache = _checkLocalCache;
    module.fetchImage = _fetchImage;
    module.preloadImage = _preloadImage;
    module.loadImagesImmediately = _loadImagesImmediately;
    module.disconnect = _disconnect;
    module.onIntersection = _onIntersection;
    module.applyImage = _applyImage;

    // execute our constructor
    return module;
  };

  return lazyloadIntersectionObserver;
});
//# sourceMappingURL=lazyloadIntersectionObserver.js.map
